import React, { useCallback } from 'react';
import DateFilterContext, { initialState } from 'core/dateFilterContext';
import coreDispatcher from 'core/dispatcher';
import { useQuery, useMutation, gql } from '@apollo/client';

export const GET_GDF_DATA = gql`
  query savedGDF($input: SavedGDFInput!) {
    savedGDF(input: $input) {
      persistGDF
    }
  }
`;

export const SAVE_GDF = gql`
  mutation saveGDF($input: SaveGDFInput!) {
    saveGDF(input: $input)
  }
`;

export const IS_ENABLED = 'isEnabled';
export const IS_CARD_FILTERING = 'isCardFiltering';
export const DATE_RANGE = 'dateRange';
export const CARD_DATE_RANGE = 'cardDateRange';
export const DATE_RANGE_VALID_VALUES = [
  'yesterday',
  'weekToDate',
  'monthToDate',
  'quarterToDate',
  'yearToDate',
  'lastWeek',
  'lastMonth',
  'lastQuarter',
  'lastYear',
  'last7Days',
  'last30Days',
  'last60Days',
  'last90Days',
  'last120Days',
  'last12Months',
];

const defaultObject = {
  [IS_ENABLED]: false,
  [IS_CARD_FILTERING]: false,
  [DATE_RANGE]: {},
  [CARD_DATE_RANGE]: {},
};

function getDashboard(dashboards, id) {
  if (dashboards.hasOwnProperty(id)) {
    return dashboards[id];
  }
  return defaultObject;
}

function toggleDashboardState(dashboards, dashboardId, key) {
  const dashboard = getDashboard(dashboards, dashboardId);
  return {
    ...dashboards,
    [dashboardId]: {
      ...dashboard,
      [key]: !dashboard?.[key],
    },
  };
}

function updateDashboardState(dashboards, dashboardId, key, value) {
  const dashboard = getDashboard(dashboards, dashboardId);
  return {
    ...dashboards,
    [dashboardId]: {
      ...dashboard,
      [key]: value,
    },
  };
}

const ContentWithDateFilterContext = (props) => {
  const { children, config } = props;
  const clientId = props.config?.client?.clientId;

  const [dashboards, setDashboards] = React.useState(initialState.dashboards);
  const [currentDashboard, setCurrentDashboard] = React.useState(null);

  const setCardDateRange = useCallback(
    ({ dashboardId, cardDateRange }) => {
      setDashboards(
        updateDashboardState(
          dashboards,
          dashboardId,
          CARD_DATE_RANGE,
          cardDateRange
        )
      );
      setCurrentDashboard(dashboardId);
    },
    [dashboards]
  );

  const setDateRange = useCallback(
    ({ dashboardId, dateRange }) => {
      setDashboards(
        updateDashboardState(dashboards, dashboardId, DATE_RANGE, dateRange)
      );
      setCurrentDashboard(dashboardId);
    },
    [dashboards]
  );

  const toggleCardFiltering = useCallback(
    (dashboardId, isCardFiltering) => {
      setDashboards(
        updateDashboardState(
          dashboards,
          dashboardId,
          IS_CARD_FILTERING,
          isCardFiltering
        )
      );
    },
    [dashboards]
  );

  const toggleDateFilter = useCallback(
    (dashboardId, callback) => {
      const newDashboards = toggleDashboardState(
        dashboards,
        dashboardId,
        IS_ENABLED
      );
      const dashboard = getDashboard(newDashboards, dashboardId);
      coreDispatcher.trigger('dashboards-data:global-date-filter:set', {
        dashboardId,
        globalDateFilter: dashboard,
      });

      if (window.location.search.length !== 0) {
        const params = new URLSearchParams(window.location.search);
        if (params.has('global-date') && !dashboard?.[IS_ENABLED]) {
          params.delete('global-date');
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          );
        }
      }

      setDashboards(newDashboards);
      if (callback) {
        callback();
      }
    },
    [dashboards]
  );

  const globalDateSavePersist = (state) => {
    updateGDFProp({
      variables: {
        input: {
          clientId,
          persistGDF: JSON.stringify(state.persistGDF),
        },
      },
    });
  };

  const { isAuthRequired = false } = config;

  const {
    loading,
    error,
    data = {},
  } = useQuery(GET_GDF_DATA, {
    variables: { input: { clientId } },
    skip: !config.isAuthRequired,
  });

  const [updateGDFProp] = useMutation(SAVE_GDF);

  React.useEffect(() => {
    if (Object.entries(dashboards).length > 0) {
      globalDateSavePersist({
        persistGDF: dashboards,
      });
    }
  }, [dashboards]);

  React.useEffect(() => {
    if (currentDashboard !== null) {
      const dashboard = getDashboard(dashboards, currentDashboard);
      let globalDateFilterValueFromParams;
      if (window.location.search.length !== 0) {
        const params = new URLSearchParams(window.location.search);
        if (params.has('global-date')) {
          globalDateFilterValueFromParams = params.get('global-date');
          if (!dashboard?.[IS_ENABLED]) {
            toggleDateFilter(currentDashboard);
          }
        }
      }
      if (dashboard?.[IS_ENABLED]) {
        if (
          globalDateFilterValueFromParams &&
          DATE_RANGE_VALID_VALUES.includes(globalDateFilterValueFromParams)
        ) {
          dashboard.dateRange.selectedDateId = globalDateFilterValueFromParams;
        }
        coreDispatcher.trigger('dashboards-data:global-date-filter:set', {
          dashboardId: currentDashboard,
          globalDateFilter: dashboard,
        });
      } else {
        coreDispatcher.trigger('dashboards-data:global-date-filter:set', {
          dashboardId: currentDashboard,
          ...defaultObject,
        });
      }
    }
  }, [dashboards[currentDashboard], currentDashboard]);

  React.useEffect(() => {
    if (!loading && !error && isAuthRequired) {
      const persistedGDFResponse = data?.savedGDF?.persistGDF || '{}';
      const persistedGDF = JSON.parse(persistedGDFResponse) || {};
      setDashboards(persistedGDF);
      window.localStorage.setItem('persistedGDF', JSON.stringify(persistedGDF));
      coreDispatcher.trigger('dashboards-data:global-date-filters:set', {
        globalDateFilters: persistedGDF,
      });
    }
  }, [loading, error, isAuthRequired]);

  if (!clientId) {
    return children;
  }

  if (!clientId || loading || error) {
    return null;
  }

  return (
    <DateFilterContext.Provider
      value={{
        dashboards,
        toggleDateFilter,
        toggleCardFiltering,
        currentDashboard,
        setCurrentDashboard,
        setDateRange,
        setCardDateRange,
      }}
    >
      {children}
    </DateFilterContext.Provider>
  );
};

export default ContentWithDateFilterContext;
